/**
 * MinistrareIA - Products Page
 * Create, manage, and track products (Stripe Payment Links / Asaas) with follow-up association.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShoppingBag, Plus, Search, Edit2, Trash2, X, Copy, Check,
  Loader2, ToggleLeft, ToggleRight, ExternalLink,
  TrendingUp, Package, PackageX, DollarSign, MoreHorizontal, FlaskConical, Download,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import productService from '../../services/global/productService';
import followupService from '../../services/global/followupService';


// ============================================================
// STATUS BADGE
// ============================================================

const ProductStatusBadge = ({ active, colors }) => {
  const config = active
    ? { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' }
    : { label: 'Inativo', bg: '#6b728020', color: '#6b7280' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: config.bg, color: config.color,
    }}>
      {config.label}
    </span>
  );
};


const ProviderBadge = ({ product, colors }) => {
  const provider = (product.provider || 'asaas').toUpperCase();
  const test = !!product.is_test;
  const pc = provider === 'STRIPE' ? '#635bff' : (provider === 'INFINITEPAY' ? '#16a34a' : '#0ea5e9');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
      <span style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.3px', backgroundColor: `${pc}20`, color: pc }}>
        {provider}
      </span>
      {test && (
        <span title="Link de teste (sandbox): não aparece na landing em produção" style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, backgroundColor: '#f59e0b20', color: '#f59e0b', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <FlaskConical size={10} /> SANDBOX
        </span>
      )}
    </span>
  );
};


// ============================================================
// STAT CARD
// ============================================================

const StatCard = ({ label, value, icon: Icon, color, colors }) => (
  <div style={{
    flex: 1, minWidth: '140px', padding: '16px 20px',
    backgroundColor: colors.bgSecondary, borderRadius: '12px',
    border: `1px solid ${colors.borderColor}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
      <span style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 500 }}>{label}</span>
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px',
        backgroundColor: `${color}15`, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} color={color} />
      </div>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color: colors.textPrimary }}>{value}</div>
  </div>
);


// ============================================================
// CONFIRM MODAL
// ============================================================

const ConfirmModal = ({ title, message, onConfirm, onCancel, loading, colors }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  }} onClick={onCancel}>
    <div style={{
      backgroundColor: colors.bgPrimary, borderRadius: '12px',
      padding: '24px', maxWidth: '400px', width: '90%',
      border: `1px solid ${colors.borderColor}`,
    }} onClick={e => e.stopPropagation()}>
      <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>{title}</h3>
      <p style={{ margin: '0 0 20px', fontSize: '14px', color: colors.textSecondary }}>{message}</p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} disabled={loading} style={{
          padding: '8px 16px', border: `1px solid ${colors.borderColor}`,
          borderRadius: '8px', backgroundColor: 'transparent',
          color: colors.textPrimary, cursor: 'pointer', fontSize: '13px',
        }}>Cancelar</button>
        <button onClick={onConfirm} disabled={loading} style={{
          padding: '8px 16px', border: 'none', borderRadius: '8px',
          backgroundColor: '#ef4444', color: 'white', cursor: 'pointer',
          fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          {loading && <Loader2 size={14} className="spin" />}
          Confirmar
        </button>
      </div>
    </div>
  </div>
);


// ============================================================
// CREATE / EDIT PRODUCT MODAL
// ============================================================

const ProductModal = ({ product, followups, onClose, onSave, colors, stripeStatus }) => {
  const isEdit = !!product?.id;
  const stripeOk = !!(stripeStatus?.live_configured || stripeStatus?.test_configured);
  const defaultLive = stripeStatus?.default_livemode ?? true;
  // modo: 'stripe_new' (cria produto+preço+link no Stripe) | 'stripe_import' (link já criado no painel) | 'asaas'
  const infinitepayOk = !!stripeStatus?.infinitepay_configured;
  const [modo, setModo] = useState(infinitepayOk ? 'infinitepay' : (stripeOk ? 'stripe_import' : 'asaas'));
  const [livemode, setLivemode] = useState(defaultLive);
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    value: product?.value || '',
    due_date_limit_days: product?.due_date_limit_days || 3,
    followup_id: product?.followup_id || '',
    stripe_payment_link_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [links, setLinks] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);

  // lista os Payment Links da conta Stripe pra importar (marca os já cadastrados)
  useEffect(() => {
    if (isEdit || modo !== 'stripe_import') return;
    const ok = livemode ? stripeStatus?.live_configured : stripeStatus?.test_configured;
    if (!ok) { setLinks([]); return; }
    let alive = true;
    setLoadingLinks(true);
    productService.stripeLinks(livemode)
      .then(r => { if (alive) setLinks(r.data || []); })
      .catch(e => { if (alive) { setLinks([]); setError(e.response?.data?.detail || 'Não consegui listar os links do Stripe'); } })
      .finally(() => { if (alive) setLoadingLinks(false); });
    return () => { alive = false; };
  }, [modo, livemode, isEdit, stripeStatus]);

  const handleSave = async () => {
    setError('');
    if (isEdit) {
      if (!form.name.trim()) { setError('Nome e obrigatorio'); return; }
    } else if (modo === 'stripe_import') {
      if (!form.stripe_payment_link_id.trim()) { setError('Escolha (ou cole o ID de) um Payment Link do Stripe'); return; }
    } else {
      if (!form.name.trim()) { setError('Nome e obrigatorio'); return; }
      if (!form.value || Number(form.value) <= 0) { setError('Valor deve ser maior que zero'); return; }
    }
    setLoading(true);
    try {
      let payload;
      if (isEdit) {
        payload = { name: form.name, description: form.description, followup_id: form.followup_id || null };
      } else if (modo === 'stripe_import') {
        payload = { provider: 'stripe', livemode, stripe_payment_link_id: form.stripe_payment_link_id.trim(), name: form.name || undefined, description: form.description, followup_id: form.followup_id || null };
      } else if (modo === 'stripe_new') {
        payload = { provider: 'stripe', livemode, name: form.name, description: form.description, value: Number(form.value), followup_id: form.followup_id || null };
      } else if (modo === 'infinitepay') {
        payload = { provider: 'infinitepay', name: form.name, description: form.description, value: Number(form.value), followup_id: form.followup_id || null };
      } else {
        payload = { provider: 'asaas', name: form.name, description: form.description, value: Number(form.value), due_date_limit_days: form.due_date_limit_days, followup_id: form.followup_id || null };
      }
      await onSave(product?.id, payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: '8px',
    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '12px',
        padding: '24px', maxWidth: '480px', width: '90%',
        border: `1px solid ${colors.borderColor}`,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            {isEdit ? 'Editar Produto' : 'Novo Produto'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {!isEdit && (
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '6px', display: 'block' }}>Como criar o link de pagamento</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  infinitepayOk && { key: 'infinitepay', label: 'InfinitePay (Pix + cartão parcelado)' },
                  stripeOk && { key: 'stripe_import', label: 'Usar link do Stripe já criado' },
                  stripeOk && { key: 'stripe_new', label: 'Criar novo no Stripe' },
                  { key: 'asaas', label: 'Asaas (antigo)' },
                ].filter(Boolean).map(o => (
                  <button key={o.key} type="button" onClick={() => { setModo(o.key); setError(''); }} style={{
                    padding: '6px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${modo === o.key ? colors.accentPrimary : colors.borderColor}`,
                    backgroundColor: modo === o.key ? `${colors.accentPrimary}20` : 'transparent',
                    color: modo === o.key ? colors.accentPrimary : colors.textSecondary,
                  }}>{o.label}</button>
                ))}
              </div>
              {modo.startsWith('stripe') && stripeStatus?.live_configured && stripeStatus?.test_configured && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: colors.textSecondary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!livemode} onChange={e => setLivemode(!e.target.checked)} />
                  <FlaskConical size={12} /> Modo sandbox (teste, sem cobrar de verdade)
                </label>
              )}
              {modo === 'infinitepay' && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: colors.textSecondary }}>
                  O link de pagamento é gerado por comprador na landing (nome + WhatsApp com país + e-mail). Recebedor: ${stripeStatus?.infinitepay_handle}.
                </p>
              )}
              {modo.startsWith('stripe') && !stripeStatus?.live_configured && stripeStatus?.test_configured && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#f59e0b' }}>Servidor só tem o Stripe de SANDBOX configurado: o link criado será de teste.</p>
              )}
            </div>
          )}

          {!isEdit && modo === 'stripe_import' && (
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>Payment Link do Stripe *</label>
              {loadingLinks ? (
                <div style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', gap: 6, alignItems: 'center' }}><Loader2 size={12} className="spin" /> Buscando links na conta Stripe…</div>
              ) : (links && links.length > 0) ? (
                <select value={form.stripe_payment_link_id} onChange={e => {
                  const l = links.find(x => x.id === e.target.value);
                  setForm({ ...form, stripe_payment_link_id: e.target.value, name: form.name || (l?.name || '') });
                }} style={inputStyle}>
                  <option value="">Escolha um link…</option>
                  {links.map(l => (
                    <option key={l.id} value={l.id} disabled={l.registered}>
                      {l.name || l.id} — R$ {Number(l.value || 0).toFixed(2)}{l.registered ? ' (já cadastrado)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>Nenhum link ativo encontrado nessa conta/modo.</p>
              )}
              <input value={form.stripe_payment_link_id} onChange={e => setForm({ ...form, stripe_payment_link_id: e.target.value })} placeholder="ou cole o ID: plink_…" style={{ ...inputStyle, marginTop: 6, fontFamily: 'monospace', fontSize: 12 }} />
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: colors.textSecondary }}>
                O link precisa coletar o telefone no checkout (é ele que inscreve o cliente no follow-up). Nome e valor vêm do Stripe.
              </p>
            </div>
          )}

          <div>
            <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>Nome do produto {modo === 'stripe_import' && !isEdit ? '(opcional)' : '*'}</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Desafio 21 Dias" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>Descricao</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Descricao do produto..."
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          {!isEdit && modo !== 'stripe_import' && (
            <>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>Valor (R$) *</label>
                <input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="97.00" min="0.01" step="0.01" style={inputStyle} />
              </div>
              {modo === 'asaas' && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>Prazo do link (dias)</label>
                  <input type="number" value={form.due_date_limit_days} onChange={e => setForm({ ...form, due_date_limit_days: Number(e.target.value) })} min="1" max="90" style={inputStyle} />
                </div>
              )}
            </>
          )}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>Follow-up associado</label>
            <select
              value={form.followup_id}
              onChange={e => setForm({ ...form, followup_id: e.target.value })}
              style={inputStyle}
            >
              <option value="">Nenhum</option>
              {followups.map(f => (
                <option key={f.id} value={f.id}>{f.name} ({f.total_days} dias)</option>
              ))}
            </select>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: colors.textSecondary }}>
              Ao pagar, o lead sera automaticamente adicionado a esse follow-up.
            </p>
          </div>
        </div>

        {error && <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#ef4444' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', border: `1px solid ${colors.borderColor}`,
            borderRadius: '8px', backgroundColor: 'transparent',
            color: colors.textPrimary, cursor: 'pointer', fontSize: '13px',
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={loading} style={{
            padding: '8px 16px', border: 'none', borderRadius: '8px',
            backgroundColor: colors.accentPrimary, color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px',
            display: 'flex', alignItems: 'center', gap: '6px',
            opacity: loading ? 0.6 : 1,
          }}>
            {loading ? <Loader2 size={14} className="spin" /> : (modo === 'stripe_import' && !isEdit ? <Download size={14} /> : <ShoppingBag size={14} />)}
            {isEdit ? 'Salvar' : (modo === 'stripe_import' ? 'Importar do Stripe' : 'Criar Produto')}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// MAIN PAGE
// ============================================================

const ProductsPage = () => {
  const { colors } = useTheme();

  // Data
  const [products, setProducts] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Action menu
  const [openMenuId, setOpenMenuId] = useState(null);

  // Copied link
  const [copiedId, setCopiedId] = useState(null);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, followupsRes] = await Promise.all([
        productService.list(),
        followupService.list(),
      ]);
      setProducts(productsRes.data || []);
      setFollowups(followupsRes.data || []);
      try {
        const st = await productService.stripeStatus();
        setStripeStatus(st.data || null);
      } catch { setStripeStatus(null); }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Computed stats
  const stats = useMemo(() => {
    const total = products.length;
    const active = products.filter(p => p.active !== false).length;
    const inactive = total - active;
    const totalSales = products.reduce((sum, p) => sum + (p.leads_count || 0), 0);
    return { total, active, inactive, totalSales };
  }, [products]);

  // Followup name map
  const followupMap = useMemo(() => {
    const map = {};
    followups.forEach(f => { map[f.id] = f.name; });
    return map;
  }, [followups]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    let result = products;
    if (statusFilter === 'active') result = result.filter(p => p.active !== false);
    if (statusFilter === 'inactive') result = result.filter(p => p.active === false);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => (p.name || '').toLowerCase().includes(q));
    }
    return result;
  }, [products, statusFilter, searchQuery]);

  // Actions
  const handleCreateProduct = async (_, data) => {
    await productService.create(data);
    await loadData();
  };

  const handleUpdateProduct = async (id, data) => {
    await productService.update(id, data);
    await loadData();
  };

  const handleToggleProduct = async (product) => {
    setActionLoading(true);
    try {
      await productService.toggle(product.id, !product.active);
      await loadData();
    } catch (err) {
      console.error('Error toggling product:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    setActionLoading(true);
    try {
      await productService.delete(productId);
      setConfirmAction(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting product:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = async (product) => {
    try {
      await navigator.clipboard.writeText(product.checkout_url || product.asaas_url);
      setCopiedId(product.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      console.error('Error copying link');
    }
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch { return '-'; }
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
      }}>
        <Loader2 size={32} color={colors.accentPrimary} className="spin" />
        <span style={{ fontSize: '14px', color: colors.textSecondary }}>Carregando produtos...</span>
      </div>
    );
  }

  const statusFilters = [
    { value: '', label: 'Todos' },
    { value: 'active', label: 'Ativos' },
    { value: 'inactive', label: 'Inativos' },
  ];

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgPrimary, overflow: 'auto',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px 0', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>Produtos</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textSecondary }}>
            Gerencie seus produtos e links de pagamento{stripeStatus?.infinitepay_configured ? ` · InfinitePay $${stripeStatus.infinitepay_handle}` : ''}{stripeStatus ? (stripeStatus.live_configured ? ' · Stripe produção' : (stripeStatus.test_configured ? ' · Stripe em SANDBOX' : '')) : ''}
          </p>
        </div>
        <button
          onClick={() => setEditingProduct({})}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
            borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
          }}
        >
          <Plus size={16} /> Novo Produto
        </button>
      </div>

      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Stat Cards */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <StatCard label="Total" value={stats.total} icon={Package} color={colors.accentPrimary} colors={colors} />
          <StatCard label="Ativos" value={stats.active} icon={TrendingUp} color="#22c55e" colors={colors} />
          <StatCard label="Inativos" value={stats.inactive} icon={PackageX} color="#6b7280" colors={colors} />
          <StatCard label="Total Vendas" value={stats.totalSales} icon={DollarSign} color="#f59e0b" colors={colors} />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {statusFilters.map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                style={{
                  padding: '6px 12px', borderRadius: '16px', fontSize: '12px',
                  fontWeight: 500, border: 'none', cursor: 'pointer',
                  backgroundColor: statusFilter === f.value ? `${colors.accentPrimary}20` : 'transparent',
                  color: statusFilter === f.value ? colors.accentPrimary : colors.textSecondary,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto',
            padding: '6px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`,
            backgroundColor: colors.bgSecondary, minWidth: '200px',
          }}>
            <Search size={14} color={colors.textSecondary} />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar produto..."
              style={{
                border: 'none', outline: 'none', backgroundColor: 'transparent',
                color: colors.textPrimary, fontSize: '13px', flex: 1,
              }}
            />
          </div>
        </div>

        {/* Table */}
        {filteredProducts.length === 0 ? (
          <div style={{
            padding: '40px', textAlign: 'center', color: colors.textSecondary, fontSize: '14px',
          }}>
            {products.length === 0
              ? 'Nenhum produto criado ainda. Clique em "Novo Produto" para comecar.'
              : 'Nenhum produto encontrado com os filtros atuais.'}
          </div>
        ) : (
          <div style={{
            borderRadius: '12px', border: `1px solid ${colors.borderColor}`, overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: colors.bgSecondary }}>
                  {['Nome', 'Valor', 'Follow-up', 'Vendas', 'Status', 'Criado em', 'Acoes'].map(h => (
                    <th key={h} style={{
                      padding: '10px 14px', textAlign: 'left', fontSize: '11px',
                      fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase',
                      letterSpacing: '0.5px', borderBottom: `1px solid ${colors.borderColor}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id} style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                    <td style={{ padding: '10px 14px', color: colors.textPrimary, fontWeight: 500 }}>
                      <div>{product.name || '-'}</div>
                      <ProviderBadge product={product} colors={colors} />
                      {product.description && (
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                          {product.description.length > 60 ? product.description.slice(0, 60) + '...' : product.description}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', color: colors.textPrimary, fontWeight: 500 }}>
                      {formatCurrency(product.value)}
                    </td>
                    <td style={{ padding: '10px 14px', color: colors.textSecondary }}>
                      {product.followup_id ? (followupMap[product.followup_id] || 'Follow-up removido') : '-'}
                    </td>
                    <td style={{ padding: '10px 14px', color: colors.textPrimary, fontWeight: 600 }}>
                      {product.leads_count || 0}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <ProductStatusBadge active={product.active !== false} colors={colors} />
                    </td>
                    <td style={{ padding: '10px 14px', color: colors.textSecondary }}>
                      {formatDate(product.created_at)}
                    </td>
                    <td style={{ padding: '10px 14px', position: 'relative' }}>
                      <button
                        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === product.id ? null : product.id); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: colors.textSecondary, padding: '4px', borderRadius: '4px',
                        }}
                      >
                        <MoreHorizontal size={18} />
                      </button>

                      {openMenuId === product.id && (
                        <div style={{
                          position: 'absolute', right: '14px', top: '36px',
                          backgroundColor: colors.bgPrimary, borderRadius: '8px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                          border: `1px solid ${colors.borderColor}`,
                          zIndex: 1000, minWidth: '200px', padding: '4px',
                        }}>
                          <button
                            onClick={() => { setOpenMenuId(null); handleCopyLink(product); }}
                            style={menuItemStyle(colors)}
                          >
                            {copiedId === product.id ? <Check size={14} color="#22c55e" /> : <Copy size={14} />}
                            {copiedId === product.id ? 'Link copiado!' : 'Copiar link'}
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); window.open(product.checkout_url || product.asaas_url, '_blank'); }}
                            style={menuItemStyle(colors)}
                          >
                            <ExternalLink size={14} /> Abrir link
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); handleToggleProduct(product); }}
                            style={menuItemStyle(colors)}
                          >
                            {product.active !== false
                              ? <><ToggleLeft size={14} /> Desativar</>
                              : <><ToggleRight size={14} /> Ativar</>
                            }
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); setEditingProduct(product); }}
                            style={menuItemStyle(colors)}
                          >
                            <Edit2 size={14} /> Editar
                          </button>
                          <div style={{ height: '1px', backgroundColor: colors.borderColor, margin: '4px 0' }} />
                          <button
                            onClick={() => { setOpenMenuId(null); setConfirmAction({ type: 'delete', product }); }}
                            style={{ ...menuItemStyle(colors), color: '#ef4444' }}
                          >
                            <Trash2 size={14} /> Remover
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ============================================
          MODALS
          ============================================ */}

      {/* Create / Edit */}
      {editingProduct !== null && (
        <ProductModal
          product={editingProduct.id ? editingProduct : null}
          followups={followups}
          onClose={() => setEditingProduct(null)}
          onSave={editingProduct.id ? handleUpdateProduct : handleCreateProduct}
          colors={colors}
          stripeStatus={stripeStatus}
        />
      )}

      {/* Confirm Delete */}
      {confirmAction?.type === 'delete' && (
        <ConfirmModal
          title="Remover produto"
          message={`Tem certeza que deseja remover "${confirmAction.product.name}"? ${confirmAction.product.provider === 'stripe' ? 'O link será DESATIVADO no Stripe (o Stripe não apaga links).' : 'O link de pagamento tambem sera removido do Asaas.'}`}
          onConfirm={() => handleDeleteProduct(confirmAction.product.id)}
          onCancel={() => setConfirmAction(null)}
          loading={actionLoading}
          colors={colors}
        />
      )}

      {/* Spinner animation */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

// Helper: menu item style
const menuItemStyle = (colors) => ({
  display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
  padding: '8px 12px', border: 'none', background: 'transparent',
  color: colors.textPrimary, fontSize: '13px', cursor: 'pointer',
  borderRadius: '6px', textAlign: 'left',
});

export default ProductsPage;
